// @ts-check
// Teclado nos menus de ação montados com Dropdown e MenuItem (WAI-ARIA, padrão "menu button"): ao abrir, o foco vai para
// o primeiro item; setas, Home e End andam; Enter ou espaço escolhem; Esc fecha e devolve o foco ao botão; Tab fecha.
// O mesmo comportamento do menu dos fluxos (flow-menu.js), para os menus que já usam MenuItem.
import { html } from 'htm/preact';
import { useEffect, useRef } from 'preact/hooks';

/**
 * Próximo item pela tecla: null quando a tecla não anda no menu.
 * @param {string} key @param {number} index item com o foco (-1: nenhum) @param {number} count
 * @returns {number | null}
 */
export function nextMenuItemIndex(key, index, count) {
  if (count <= 0) return null;
  if (key === 'ArrowDown') return index < 0 ? 0 : (index + 1) % count;
  if (key === 'ArrowUp') return index < 0 ? count - 1 : (index - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

/** @param {HTMLElement | null} root */
const itemsOf = (root) => /** @type {HTMLElement[]} */ ([...(root?.querySelectorAll('.menu-item:not([aria-disabled="true"])') ?? [])]);

/** @param {{ current: HTMLElement | null }} anchorRef */
function focusAnchor(anchorRef) {
  const anchor = anchorRef.current;
  const target = anchor && anchor.tagName !== 'BUTTON' ? /** @type {HTMLElement | null} */ (anchor.querySelector('button')) : anchor;
  target?.focus({ preventScroll: true });
}

/**
 * Itens do menu com o teclado. Envolve os MenuItem dentro do DropdownMenuItems (o invólucro não muda o layout).
 * @param {{ isOpen: boolean, anchorRef: { current: HTMLElement | null }, onClose: () => void, children?: any }} props
 */
export function MenuKeyboardScope({ isOpen, anchorRef, onClose, children }) {
  const scopeRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  useEffect(() => {
    if (!isOpen) return undefined;
    const frame = requestAnimationFrame(() => itemsOf(scopeRef.current)[0]?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);
  /** @param {KeyboardEvent} event */
  const handleKeyDown = (event) => {
    const items = itemsOf(scopeRef.current);
    const index = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    const next = nextMenuItemIndex(event.key, index, items.length);
    if (next !== null) {
      event.preventDefault();
      items[next]?.focus({ preventScroll: true });
    } else if ((event.key === 'Enter' || event.key === ' ') && index >= 0) {
      event.preventDefault();
      items[index].click();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      requestAnimationFrame(() => focusAnchor(anchorRef));
    } else if (event.key === 'Tab') {
      onClose();
    }
  };
  return html`<div class="menu-keyboard-scope" ref=${scopeRef} onKeyDown=${handleKeyDown}>${children}</div>`;
}
